/**
 * Rumelt Strategy Kernel Module — TypeScript Types
 *
 * Implements the Kernel of Good Strategy framework (Richard Rumelt):
 *   Diagnosis → Guiding Policy → Coherent Actions
 *
 * Coexists with the legacy strategy.ts (vision/mission/drivers).
 * Users choose which module to use per workspace/strategy.
 */

// ============================================================================
// Enums / Union Types
// ============================================================================

export type KernelStatus = 'draft' | 'active' | 'archived';
export type KernelTimeHorizon = 'quarterly' | 'annual' | 'multi-year';
export type ProximateObjectiveStatus = 'draft' | 'active' | 'achieved' | 'deferred';
export type AssumptionStatus = 'active' | 'confirmed' | 'invalidated';

// ============================================================================
// Coherent Action (embedded in strategy_kernels.coherent_actions JSONB)
// ============================================================================

export interface CoherentAction {
  id: string;
  title: string;
  description?: string;
  ownerId?: string;
  order: number;
}

// ============================================================================
// Strategy Kernel (root object)
// ============================================================================

export interface StrategyKernel {
  id: string;
  workspaceId: string;
  name: string;
  description?: string;

  // The three mandatory parts of a good strategy
  diagnosis: string;
  guidingPolicy: string;
  coherentActions: CoherentAction[]; // max 5, enforced at store layer

  // Selected crux challenge (FK → strategy_challenges.id)
  cruxId?: string;

  status: KernelStatus;
  timeHorizon: KernelTimeHorizon;

  // Links to existing prioritization cycles
  linkedCycleIds: string[];

  ownerId?: string;
  createdBy: string | null;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// Strategy Challenge (ASC — Addressable Strategic Challenge)
// ============================================================================

export interface StrategyChallenge {
  id: string;
  kernelId: string;
  workspaceId: string;

  title: string;
  description?: string;

  /** 1–10: how much it threatens core or represents an opportunity */
  importance: number;
  /** 1–10: degree to which the challenge is actually solvable */
  addressability: number;

  /** True for the single challenge selected as the strategic Crux */
  isCrux: boolean;

  createdBy: string | null;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// Proximate Objective
// ============================================================================

export interface ProximateObjective {
  id: string;
  kernelId: string;
  workspaceId: string;

  /** Self-referencing parent for multi-level cascade */
  parentId?: string;

  /** Which team/division owns this objective */
  orgUnitId?: string;
  orgUnitName?: string;

  title: string;
  description?: string;

  /** 0 = enterprise, 1 = division, 2 = team */
  level: number;

  status: ProximateObjectiveStatus;
  dueDate?: string; // ISO date string

  createdBy: string | null;
  createdAt: Date;
  updatedAt: Date;

  /** Client-side tree hydration — populated by getObjectivesTree() */
  children?: ProximateObjective[];
}

// ============================================================================
// Strategy Take (Responsibility Grid entry)
// ============================================================================

export interface TakeAction {
  id: string;
  title: string;
  ownerId?: string;
  dueDate?: string; // ISO date string
}

export interface TakeInterdependency {
  id: string;
  fromUnitId?: string;
  fromUnitName?: string;
  deliverable: string;
  dueDate?: string; // ISO date string
}

export interface TakeDeliverable {
  id: string;
  title: string;
  description?: string;
  dueDate?: string; // ISO date string
}

export interface StrategyTake {
  id: string;
  kernelId: string;
  proximateObjectiveId?: string;
  workspaceId: string;

  orgUnitId?: string;
  orgUnitName?: string;

  /** Max 5 per take, enforced at store layer */
  committedActions: TakeAction[];
  interdependencies: TakeInterdependency[];
  deliverables: TakeDeliverable[];

  milestoneDate?: string; // ISO date string

  createdBy: string | null;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// Strategy Assumption (Navigation / Tracker)
// ============================================================================

export interface StrategyAssumption {
  id: string;
  kernelId: string;
  workspaceId: string;

  assumptionText: string;

  /** When leadership must review and potentially adjust strategy */
  reviewDate?: string; // ISO date string

  status: AssumptionStatus;
  reviewNotes?: string;

  createdBy: string | null;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// Input Types (for create/update operations)
// ============================================================================

export interface CreateKernelInput {
  name: string;
  description?: string;
  diagnosis?: string;
  guidingPolicy?: string;
  timeHorizon?: KernelTimeHorizon;
  ownerId?: string;
  linkedCycleIds?: string[];
}

export interface UpdateKernelInput {
  name?: string;
  description?: string;
  diagnosis?: string;
  guidingPolicy?: string;
  coherentActions?: CoherentAction[];
  cruxId?: string | null;
  status?: KernelStatus;
  timeHorizon?: KernelTimeHorizon;
  linkedCycleIds?: string[];
  ownerId?: string;
}

export interface CreateChallengeInput {
  kernelId: string;
  title: string;
  description?: string;
  importance?: number;
  addressability?: number;
}

export interface UpdateChallengeInput {
  title?: string;
  description?: string;
  importance?: number;
  addressability?: number;
}

export interface CreateProximateObjectiveInput {
  kernelId: string;
  parentId?: string;
  orgUnitId?: string;
  title: string;
  description?: string;
  level?: number;
  dueDate?: string;
}

export interface UpdateProximateObjectiveInput {
  parentId?: string | null;
  orgUnitId?: string | null;
  title?: string;
  description?: string;
  level?: number;
  status?: ProximateObjectiveStatus;
  dueDate?: string | null;
}

export interface CreateTakeInput {
  kernelId: string;
  proximateObjectiveId?: string;
  orgUnitId?: string;
  milestoneDate?: string;
}

export interface UpdateTakeInput {
  orgUnitId?: string | null;
  committedActions?: TakeAction[];
  interdependencies?: TakeInterdependency[];
  deliverables?: TakeDeliverable[];
  milestoneDate?: string | null;
}

export interface CreateAssumptionInput {
  kernelId: string;
  assumptionText: string;
  reviewDate?: string;
}

export interface UpdateAssumptionInput {
  assumptionText?: string;
  reviewDate?: string | null;
  status?: AssumptionStatus;
  reviewNotes?: string;
}

// ============================================================================
// Helper utilities
// ============================================================================

export const MAX_COHERENT_ACTIONS = 5;
export const MAX_TAKE_ACTIONS = 5;

/** Challenges in the top-right quadrant (high importance + high addressability) */
export function isHighPriorityChallenge(c: StrategyChallenge): boolean {
  return c.importance >= 6 && c.addressability >= 6;
}

export function getKernelStatusLabel(status: KernelStatus): string {
  const labels: Record<KernelStatus, string> = {
    draft: 'Draft',
    active: 'Active',
    archived: 'Archived',
  };
  return labels[status];
}

export function getKernelTimeHorizonLabel(horizon: KernelTimeHorizon): string {
  const labels: Record<KernelTimeHorizon, string> = {
    quarterly: 'Quarterly',
    annual: 'Annual',
    'multi-year': 'Multi-Year',
  };
  return labels[horizon];
}

export function getAssumptionStatusLabel(status: AssumptionStatus): string {
  const labels: Record<AssumptionStatus, string> = {
    active: 'Active',
    confirmed: 'Confirmed',
    invalidated: 'Invalidated',
  };
  return labels[status];
}

export function getProximateObjectiveStatusLabel(status: ProximateObjectiveStatus): string {
  const labels: Record<ProximateObjectiveStatus, string> = {
    draft: 'Draft',
    active: 'Active',
    achieved: 'Achieved',
    deferred: 'Deferred',
  };
  return labels[status];
}

/** Build nested tree from flat proximate objectives array */
export function buildObjectivesTree(flat: ProximateObjective[]): ProximateObjective[] {
  const map = new Map<string, ProximateObjective>();
  const roots: ProximateObjective[] = [];

  flat.forEach(obj => map.set(obj.id, { ...obj, children: [] }));

  flat.forEach(obj => {
    const node = map.get(obj.id)!;
    if (obj.parentId && map.has(obj.parentId)) {
      const parent = map.get(obj.parentId)!;
      parent.children = parent.children ?? [];
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
}

/** Revenue / goal language detection for Diagnosis / Guiding Policy fields */
export const REVENUE_LANGUAGE_PATTERN = /(\d+%|\$\d|\bgrow\b|\bgrowth\b|\brevenue\b|\btarget\b|\bgoal\b)/i;

export function detectsRevenueLang(text: string): boolean {
  return REVENUE_LANGUAGE_PATTERN.test(text);
}
